/**
 * KnowledgeGapsPage — "Câu hỏi AI chưa trả lời được" (Nhân viên AI › Theo dõi, `/ai/theo-doi#cau-hoi`;
 * link cũ `/ai/knowledge-gaps` chuyển hướng về đó). `embedded` = nằm trong tab, không có tiêu đề trang.
 * Danh sách các câu hỏi AI không đủ dữ kiện để trả lời. Owner/admin xử lý:
 *  - Giải quyết (resolve): nhập nội dung → tạo tri thức.
 *  - Bỏ qua (dismiss).
 */
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CircleHelp, Check, X, BookOpenCheck } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/misc'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { KnowledgeBaseSelect } from './knowledge/kb-base-select'
import { KbCategorySelect } from './knowledge/kb-category-select'
import { useAiPerms } from './use-ai-perms'
import {
  aiKeys, useKnowledgeGaps, dismissGap, resolveGap,
  type KnowledgeGap, type GapStatus,
} from '@/hooks/use-ai'

const STATUS_LABEL: Record<GapStatus, string> = {
  get open() { return i18n.t('aiKnow:gaps.status.open') },
  get resolved() { return i18n.t('aiKnow:gaps.status.resolved') },
  get dismissed() { return i18n.t('aiKnow:gaps.status.dismissed') },
}

const GAP_TYPE_LABEL: Record<string, string> = {
  get missing_info() { return i18n.t('aiKnow:gaps.type.missing_info') },
  get needs_knowledge() { return i18n.t('aiKnow:gaps.type.needs_knowledge') },
  get needs_staff() { return i18n.t('aiKnow:gaps.type.needs_staff') },
}

function statusBadge(s: GapStatus) {
  if (s === 'resolved') return <Badge variant="success">{STATUS_LABEL[s]}</Badge>
  if (s === 'dismissed') return <Badge variant="secondary">{STATUS_LABEL[s]}</Badge>
  return <Badge variant="warning">{STATUS_LABEL[s]}</Badge>
}

export function KnowledgeGapsPage({ embedded = false }: { embedded?: boolean } = {}) {
  // Xử lý / bỏ qua lỗ hổng kiến thức: knowledge-gap-routes đòi `ai.update`. Không có quyền → không hiện nút.
  const { t } = useTranslation('aiKnow')
  const { canEdit } = useAiPerms()
  const qc = useQueryClient()

  const [status, setStatus] = useState<GapStatus>('open')
  const { data, isLoading, isError } = useKnowledgeGaps(status)

  const [resolving, setResolving] = useState<KnowledgeGap | null>(null)
  const [content, setContent] = useState('')
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  // Mặc định danh mục phổ biến nhất — mục không danh mục thì nhân viên AI bị giới hạn danh mục không thấy.
  // Kho nhận tri thức ('' = "Kho mặc định") + danh mục trong kho.
  const [baseId, setBaseId] = useState('')
  const [categoryId, setCategoryId] = useState('')

  const total = data?.total ?? 0

  function openResolve(gap: KnowledgeGap) {
    setResolving(gap)
    setCategoryId('')
    setTitle(gap.question.slice(0, 120))
    setContent(gap.suggestion ?? '')
  }

  async function handleResolve() {
    if (!resolving) return
    if (!content.trim()) {
      toast.error(t('gaps.contentRequired'))
      return
    }
    setBusy(true)
    try {
      await resolveGap(resolving.id, content.trim(), title.trim() || undefined, categoryId || null, baseId || null)
      await qc.invalidateQueries({ queryKey: aiKeys.knowledgeGaps(status) })
      toast.success(t('gaps.added'))
      setResolving(null)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleDismiss(gap: KnowledgeGap) {
    setBusy(true)
    try {
      await dismissGap(gap.id)
      await qc.invalidateQueries({ queryKey: aiKeys.knowledgeGaps(status) })
      toast.success(t('gaps.dismissed'))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  const cols: Column<KnowledgeGap>[] = useMemo(() => [
    {
      key: 'question', header: t('gaps.col.question'),
      cell: (r) => (
        <div>
          <div className="font-medium">{r.question}</div>
          {r.suggestion && <div className="text-xs text-muted-foreground line-clamp-1">{t('gaps.suggestion', { text: r.suggestion })}</div>}
        </div>
      ),
    },
    { key: 'gapType', header: t('gaps.col.type'), cell: (r) => <Badge variant="outline">{GAP_TYPE_LABEL[r.gapType] ?? r.gapType}</Badge> },
    { key: 'occurrences', header: t('gaps.col.occurrences'), align: 'right', cell: (r) => r.occurrences },
    {
      key: 'lastSeenAt', header: t('gaps.col.lastSeen'),
      cell: (r) => <span className="text-muted-foreground">{new Date(r.lastSeenAt).toLocaleString(currentLocale())}</span>,
    },
    { key: 'status', header: t('gaps.col.status'), cell: (r) => statusBadge(r.status) },
    {
      key: 'actions', header: '', align: 'right',
      cell: (r) =>
        canEdit && r.status === 'open' ? (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="icon" onClick={() => openResolve(r)} title={t('gaps.addAnswer')}>
              <Check className="h-4 w-4 text-success" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => handleDismiss(r)} disabled={busy} title={t('common:actions.skip')}>
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, busy, status, t])

  return (
    <div className="space-y-6">
      {embedded ? (
        <p className="text-sm text-muted-foreground">
          {t('gaps.description')}
        </p>
      ) : (
        <PageHeader
          title={t('gaps.title')}
          description={t('gaps.description')}
        />
      )}

      <div className="flex items-center justify-between">
        <Tabs value={status} onValueChange={(v) => setStatus(v as GapStatus)}>
          <TabsList>
            <TabsTrigger value="open">{t('gaps.status.open')}</TabsTrigger>
            <TabsTrigger value="resolved">{t('gaps.status.resolved')}</TabsTrigger>
            <TabsTrigger value="dismissed">{t('gaps.status.dismissed')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="text-sm text-muted-foreground"><Trans t={t} i18nKey="gaps.total" values={{ n: total }} components={{ b: <b className="text-foreground" /> }} /></span>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isError ? (
            <DataTable columns={cols} rows={[]} rowKey={(r) => r.id} emptyTitle={t('gaps.loadError')} />
          ) : (
            <DataTable
              columns={cols} rows={data?.data ?? []} rowKey={(r) => r.id}
              loading={isLoading} emptyTitle={t('gaps.empty')}
            />
          )}
        </CardContent>
      </Card>

      {/* Dialog giải quyết */}
      <Dialog open={!!resolving} onOpenChange={(o) => !o && setResolving(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpenCheck className="h-4 w-4 text-primary" /> {t('gaps.resolveTitle')}
            </DialogTitle>
            <DialogDescription className="flex items-start gap-2">
              <CircleHelp className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{resolving?.question}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label>{t('gaps.titleLabel')}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('gaps.titleLabel')} />
            </div>
            <div className="grid gap-2">
              <Label>{t('gaps.baseLabel')}</Label>
              <KnowledgeBaseSelect value={baseId} onChange={(id) => { setBaseId(id); setCategoryId('') }} autoLabel={t('gaps.defaultBase')} />
              {baseId && (
                <KbCategorySelect knowledgeBaseId={baseId} kind="qa" value={categoryId} onChange={setCategoryId} />
              )}
              <p className="text-[11px] text-muted-foreground">{t('gaps.baseHint')}</p>
            </div>
            <div className="grid gap-2">
              <Label>{t('gaps.answerLabel')}</Label>
              <Textarea
                className="min-h-[140px]" value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t('gaps.answerPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolving(null)}>{t('common:actions.cancel')}</Button>
            <Button onClick={handleResolve} disabled={busy || !content.trim()}>
              <Check className="h-4 w-4" /> {busy ? t('gaps.saving') : t('gaps.addToBase')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
